/**
 * Centralized security policy defaults.
 * Values here are the fallback when an organization has no explicit
 * security_settings row. Never hardcode these numbers in pages.
 */

export type MfaPolicy =
  | "optional"
  | "required_admins"
  | "required_privileged"
  | "required_all_users";

export const MFA_POLICY_LABELS: Record<MfaPolicy, string> = {
  optional: "Optional (user choice)",
  required_admins: "Required for organization admins",
  required_privileged: "Required for privileged users (government default)",
  required_all_users: "Required for all users",
};

export const MFA_POLICY_DESCRIPTIONS: Record<MfaPolicy, string> = {
  optional: "Users may enroll an authenticator app but are never forced to.",
  required_admins: "Only users holding the admin role must enroll TOTP.",
  required_privileged:
    "Admins and managers (case, export, security and privileged record access) must enroll TOTP.",
  required_all_users: "Every member of the organization must enroll TOTP before using the app.",
};

export const SECURITY_DEFAULTS = {
  /** Default org MFA policy when no settings row exists. */
  mfaPolicy: "required_privileged" as MfaPolicy,
  /** Days a newly created privileged user may sign in before MFA is enforced. */
  mfaGracePeriodDays: 7,
  /** Idle timeout for general users, in minutes. */
  sessionTimeoutMinutes: 30,
  /** Tighter idle timeout for privileged/government users, in minutes. */
  privilegedSessionTimeoutMinutes: 15,
  /** Seconds of warning shown before an idle logout. */
  sessionWarningSeconds: 60,
  /** Failed authentication attempts before progressive throttling. */
  maxLoginAttempts: 5,
  /** Temporary lockout window after repeated failures, in minutes. */
  lockoutDurationMinutes: 30,
} as const;

/** Routes that must stay reachable while an MFA enrollment block is active. */
export const MFA_EXEMPT_PATHS = [
  "/app/mfa-setup",
  "/app/profile",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
];

export function isMfaExemptPath(pathname: string): boolean {
  return MFA_EXEMPT_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
